import {
  Box,
  Drawer,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useI18n } from 'iomart-hooks';
import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import { bgBlur } from 'src/utils/cssStyles';
import type { ServerFormError } from 'src/utils/serverFormErrors';

type Props = {
  open: boolean;
  onClose: () => void;
  errors: ServerFormError[];
  onFocusField: (path: string) => void;
  /** Map field path -> human-readable label (per entity) */
  fieldLabels?: Record<string, string>;
  /** Если задан — подпись поля берётся из него (path → строка), иначе из fieldLabels */
  getFieldLabel?: (path: string) => string;
  /** Format raw server message to human-readable (per entity); undefined = use default */
  formatMessage?: (message: string, path?: string) => string | undefined;
  /** Optional: translate message by key; default tries t(message) and falls back to message */
  translateMessage?: (message: string) => string;
};

export default function FormErrorsDrawer({
  open,
  onClose,
  errors,
  onFocusField,
  fieldLabels = {},
  getFieldLabel: getFieldLabelProp,
  formatMessage: formatMessageProp,
  translateMessage: translateMessageProp,
}: Props) {
  const { t } = useI18n(false);
  const translateMessage =
    translateMessageProp ??
    ((message: string) => {
      const translated = t(message);
      return translated !== message ? translated : message;
    });
  const displayMessage = (message: string, path?: string) => {
    const formatted = formatMessageProp?.(message, path);
    return formatted != null ? formatted : translateMessage(message);
  };
  const displayFieldLabel = (path: string, fallbackLabel?: string) => {
    if (getFieldLabelProp) return getFieldLabelProp(path);
    if (fieldLabels[path]) return t(fieldLabels[path]);
    if (fallbackLabel) return t(fallbackLabel);
    const prefixMatch = Object.keys(fieldLabels)
      .filter((key) => path.startsWith(key + '.') || path === key)
      .sort((a, b) => b.length - a.length)[0];
    if (prefixMatch) return t(fieldLabels[prefixMatch]);
    return path;
  };

  if (!open) return null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      ModalProps={{
        hideBackdrop: true,
        disableEnforceFocus: true,
        disableAutoFocus: true,
        disableScrollLock: true,
        slotProps: {
          backdrop: { sx: { pointerEvents: 'none' } },
          root: { style: { pointerEvents: 'none' } },
        },
      }}
      PaperProps={{
        sx: (theme) => ({
          width: { xs: '100%', sm: 480 },
          maxWidth: '100%',
          pointerEvents: 'auto',
          ...bgBlur({
            color: theme.palette.background.default,
            blur: 12,
            opacity: 0.92,
          }),
          borderLeft: 1,
          borderColor: 'transparent',
        }),
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">{t('Validation errors')}</Typography>
            <Tooltip title={t('Close')}>
              <IconButton size="small" onClick={onClose}>
                <Iconify icon="eva:close-fill" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>

        <Scrollbar sx={{ flex: 1, minHeight: 0 }}>
          <TableContainer sx={{ overflow: 'visible' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ minWidth: 140 }}>{t('Field')}</TableCell>
                  <TableCell>{t('Error')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {errors.map((row, idx) => (
                  <TableRow
                    key={`${row.path}-${idx}`}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => onFocusField(row.path)}
                  >
                    <TableCell sx={{ verticalAlign: 'top' }}>
                      {displayFieldLabel(row.path, row.fieldLabel)}
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'top' }}>
                      {displayMessage(row.message, row.path)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Scrollbar>
      </Box>
    </Drawer>
  );
}
